import { Observer } from 'mobx-react';
import * as React from 'react';

interface IObservableConsumerProps<T> {
    children?: (value: T) => React.ReactElement;
}

const makeObservableConsumer = function<T>(context: React.Context<T>) {
    return (props: IObservableConsumerProps<T>) => {
        return (
            <context.Consumer>
                {contextInstance =>
                    <Observer>{
                        () => {
                            return props.children(contextInstance);
                        }
                    }
                    </Observer>
                }
            </context.Consumer>
        );
    };
};

type connectFunction<TContextType> = <TContextProps, TOwnProps = {}>(
    ComponentClass: React.ComponentClass<TContextProps & TOwnProps> | React.FunctionComponent<TContextProps & TOwnProps>,
    mapperFunction: (viewModel: TContextType) => TContextProps,
) => (props: TOwnProps) => React.ReactElement;

/**
 * utility function, which build 'connect' function for arbitrary context
 * @param context
 */
const makeConnect = function<TContextType>(context: React.Context<TContextType>): connectFunction<TContextType> {
    const Consumer = makeObservableConsumer(context);
    return <TContextProps, TOwnProps = {}>(
        ComponentClass : React.ComponentClass<TContextProps & TOwnProps> | React.FunctionComponent<TContextProps & TOwnProps>,
        mapperFunction : (context: TContextType) => TContextProps,
    ) => {
        return (props: TOwnProps) => (
            <Consumer>{vm => {
                const contextProps = mapperFunction(vm);
                const newProps = {
                    ...props,
                    ...contextProps,
                };
                return <ComponentClass { ...newProps }/>;
            }}</Consumer>
        );
    };
};

export { makeObservableConsumer, makeConnect };
